/**
 * PdfMetadata.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

// Metadata of the exported PDF documents, in one place.
//
// Two layers are written and they say the same thing:
//   - the Info dictionary (doc.setProperties), what file managers and most
//     "document properties" dialogs read;
//   - an XMP packet (doc.addMetadata), what exiftool, Preview and every reader
//     following the XMP specification read.
//
// /Producer is the one field that cannot be set: jsPDF writes "jsPDF <version>"
// itself, from a closure with no hook to change it (dist/jspdf.es.js, putInfo,
// and setProperties only accepts title/subject/author/keywords/creator). So the
// application is named where the format lets it be named: /Creator of the Info
// dictionary - which is exactly what that field means, the application the
// document comes from - and pdf:Producer of the XMP packet, which carries
// "MV-Plan <version> (jsPDF <version>)": the tool that produced the document,
// and the library it used.
//
// The strings are deliberately in fixed English while the document itself is
// translated: metadata is read by tools and indexers, and a keyword that
// changes with the interface language cannot be searched for.

import { jsPDF } from 'jspdf'
import { APP_VERSION } from './SavedPlans.js'
import { getMaxDepth, getBottomTime, exposureShown } from './planUtils.js'
import { Gas } from '../core/Gas.js'

export const APP_NAME = 'MV-Plan'

// What goes in /Creator, and in xmp:CreatorTool
export const PDF_CREATOR = `${APP_NAME} ${APP_VERSION}`

// What goes in pdf:Producer. jsPDF is kept next to MV-Plan: it did produce the
// bytes, and a reader hitting a malformed PDF must be able to tell which of the
// two to blame.
export const PDF_PRODUCER = `${PDF_CREATOR} (jsPDF ${jsPDF.version})`

// Namespace of the dive fields of the XMP packet. A URN and not an http URI:
// an XMP namespace is an identifier, never fetched, and pointing at a domain
// the project does not own would promise a schema nobody serves.
export const MVPLAN_XMP_NS = 'urn:mvplan:pdf-metadata:1.0/'

// Algorithm names as they are written in the literature. An unknown value is
// reported as it is stored rather than silently renamed: a plan computed with
// something else must not claim to be a ZHL-16C.
const ALGORITHM_LABELS = {
  zhl16b: 'Buhlmann ZHL-16B',
  zhl16c: 'Buhlmann ZHL-16C'
}

// Water of the dive site, named for a machine. The preset labels are the
// English ones on purpose (see the header note on the language).
const WATER_LABELS = {
  fresh: 'fresh water',
  brackish: 'brackish water',
  salt: 'sea water',
  red_sea: 'Red Sea water'
}

const BASE_KEYWORDS = ['dive planning', 'decompression', 'dive plan', APP_NAME]

// XML names accepted for the mvplan: fields. Everything written here comes from
// this file, so a rejected key means a typo, and a typo would produce a packet
// no XMP reader can parse.
const XML_NAME = /^[A-Za-z][A-Za-z0-9]*$/

// A4 portrait document, the format every sheet of the application uses.
// Callers that chain several sheets into one file (a day of diving) create the
// document here and pass it on, so the metadata is written once, on the whole
// document, instead of once per dive - jsPDF keeps the first XMP packet it is
// given and ignores the next ones.
export function createPdfDocument() {
  return new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
}

function escapeXml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

// ISO 8601 to the second, which is what xmp:CreateDate expects
function xmpDate(date) {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

function text(value) {
  if (value === undefined || value === null) return ''
  return String(value).trim()
}

// Duration in minutes, printed for a reader and not for a computation
function minutesLabel(minutes) {
  if (!Number.isFinite(minutes)) return ''
  return `${Math.round(minutes)} min`
}

function hoursLabel(minutes) {
  if (!Number.isFinite(minutes)) return ''
  const hours = minutes / 60
  return Number.isInteger(hours) ? `${hours} h` : `${minutesLabel(minutes)}`
}

function algorithmLabel(settings) {
  const key = text(settings?.algorithm).toLowerCase()
  if (!key) return ALGORITHM_LABELS.zhl16c
  if (!ALGORITHM_LABELS[key]) {
    console.error('PdfMetadata.algorithmLabel: unknown algorithm "%s", reported as stored', key)
    return key.toUpperCase()
  }
  return ALGORITHM_LABELS[key]
}

// Gradient factors as whole percentages, or null when they cannot be read
function gradientFactors(settings) {
  const low = Number(settings?.gfLow)
  const high = Number(settings?.gfHigh)
  if (!Number.isFinite(low) || !Number.isFinite(high)) {
    console.error('PdfMetadata.gradientFactors: unusable gradient factors (low=%o, high=%o), left out of the metadata',
      settings?.gfLow, settings?.gfHigh)
    return null
  }
  return { low: Math.round(low * 100), high: Math.round(high * 100) }
}

// The pair as the diver reads it on every screen of the application
function gfLabel(settings) {
  const gf = gradientFactors(settings)
  return gf ? `${gf.low}/${gf.high}` : ''
}

// Water of the environment, or an empty string when it cannot be named. Never
// throws: Gas.waterDensity refuses an unusable value, and an export must not be
// lost over a metadata field.
function waterLabel(environment) {
  const waterType = environment?.waterType
  if (waterType === undefined || waterType === null) return WATER_LABELS.salt
  if (WATER_LABELS[waterType]) return WATER_LABELS[waterType]

  try {
    return `${Gas.waterDensity(waterType)} g/l`
  } catch (e) {
    console.error('PdfMetadata.waterLabel: unusable water type %o (%s), water left out of the metadata',
      waterType, e.message)
    return ''
  }
}

function altitudeLabel(environment) {
  const altitude = Number(environment?.altitude)
  if (!Number.isFinite(altitude) || altitude === 0) return ''
  return `${Math.round(altitude)} m`
}

// Names of the mixes actually breathed on the profile, in the order they are
// first used. The configured bottles are not what the document is about: a
// bottle the plan never reaches did not take part in this dive.
function breathedGases(plan) {
  const names = []
  for (const segment of plan?.segments || []) {
    const name = text(segment.gas)
    if (name && !names.includes(name)) names.push(name)
  }
  return names
}

// Deepest stop of the plan, in metres, or null when the ascent holds none
function firstStopDepth(plan) {
  const stops = plan?.stops || []
  if (stops.length === 0) return null
  return Math.max(...stops.map(stop => Number(stop.depth) || 0))
}

function totalStopTime(plan) {
  const stops = plan?.stops || []
  return stops.reduce((sum, stop) => sum + (Number(stop.time) || 0), 0)
}

// Surface interval fields, shared by a single dive and by a day: they are read
// off the plan the diver leaves the water with, in both cases.
function surfaceIntervalFields(plan) {
  const fields = {}
  if (plan?.desatTime === null) {
    fields.desaturation = 'over 48 h'
  } else if (Number.isFinite(plan?.desatTime)) {
    fields.desaturation = minutesLabel(plan.desatTime)
  }

  if (Number.isFinite(plan?.noFlyTime)) {
    const delay = hoursLabel(plan.noFlyTime)
    fields.noFly = plan.noFlyMinimum === true ? `more than ${delay}` : delay
  }
  if (text(plan?.noFlyReason)) fields.noFlyCase = text(plan.noFlyReason)
  return fields
}

// Fields every document carries, dive or day
function contextFields(settings, environment) {
  const fields = {
    application: APP_NAME,
    applicationVersion: APP_VERSION,
    algorithm: algorithmLabel(settings)
  }

  // Two fields and not one "30/85": readers that guess the type of a value
  // (exiftool does) read a lone "30/85" as the rational number 0.3529.
  const gf = gradientFactors(settings)
  if (gf) {
    fields.gradientFactorLow = `${gf.low} %`
    fields.gradientFactorHigh = `${gf.high} %`
  }

  const water = waterLabel(environment)
  if (water) fields.water = water

  const altitude = altitudeLabel(environment)
  if (altitude) fields.altitude = altitude

  const site = text(settings?.diveSite)
  if (site) fields.diveSite = site

  const divers = text(settings?.diverNames)
  if (divers) fields.divers = divers

  return fields
}

/**
 * Metadata of a single dive sheet, from the plan the exporter is printing.
 *
 * @param {Object} args
 * @param {Object} args.plan - Computed plan, { ccr, bailout } as the views hold it
 * @param {Object} args.settings - Shared settings the plan was computed with
 * @param {Object} args.ccr - Rebreather configuration, { enabled, ... }
 * @param {Object} args.environment - Dive environment, { altitude, waterType }
 * @returns {Object} { title, author, subject, keywords, fields }
 */
export function divePdfMetadata({ plan, settings = {}, ccr = {}, environment = {} } = {}) {
  const isCCR = ccr?.enabled === true
  const dive = isCCR ? plan?.ccr : plan?.bailout
  const site = text(settings?.diveSite)
  const author = text(settings?.diverNames)
  const fields = contextFields(settings, environment)

  if (!dive) {
    console.error('PdfMetadata.divePdfMetadata: the plan carries no %s profile (%o), the document only names the application',
      isCCR ? 'CCR' : 'open circuit', plan)
    return {
      title: [APP_NAME, site, 'dive plan'].filter(Boolean).join(' - '),
      author,
      subject: `${algorithmLabel(settings)} dive plan`,
      keywords: [...BASE_KEYWORDS],
      fields
    }
  }

  const maxDepth = getMaxDepth(dive)
  const bottomTime = getBottomTime(dive)
  const runtime = Math.ceil(dive.runtime || 0)
  const tts = Math.round(dive.tts || 0)
  const exposure = exposureShown(dive)
  const gases = breathedGases(dive)
  const gf = gfLabel(settings)
  const modeLabel = isCCR ? 'closed circuit rebreather' : 'open circuit'
  const algorithm = algorithmLabel(settings)

  const subject = [
    `${algorithm} decompression plan`,
    gf ? `GF ${gf}` : '',
    modeLabel,
    `max depth ${maxDepth} m`,
    `bottom time ${bottomTime} min`,
    `runtime ${runtime} min`,
    `TTS ${tts} min`
  ].filter(Boolean).join(', ')

  Object.assign(fields, {
    documentType: 'dive plan',
    mode: isCCR ? 'CCR' : 'OC',
    maxDepth: `${maxDepth} m`,
    bottomTime: minutesLabel(bottomTime),
    runtime: minutesLabel(runtime),
    timeToSurface: minutesLabel(tts),
    decompressionRequired: dive.decoObligation === true ? 'yes' : 'no',
    // The exposure of this dive alone, as result.cns and result.otu mean it.
    // The totals of the series are separate fields below, so a reader can tell
    // what the dive cost from what the day has already spent.
    cns: `${Math.round(dive.cns || 0)} %`,
    otu: String(Math.round(dive.otu || 0))
  })

  if (gases.length > 0) fields.gases = gases.join(', ')

  const deepestStop = firstStopDepth(dive)
  if (deepestStop !== null) {
    fields.deepestStop = `${deepestStop} m`
    fields.stopCount = String(dive.stops.length)
    fields.totalStopTime = minutesLabel(totalStopTime(dive))
  }

  if (isCCR) {
    fields.setpointMode = ccr.mode === 'fixed' ? 'fixed' : 'dynamic'
    fields.setpoint = ccr.mode === 'fixed'
      ? String(ccr.fixedSetpoint)
      : `${ccr.dynamicSetpoints?.descent}/${ccr.dynamicSetpoints?.bottom}/${ccr.dynamicSetpoints?.ascent}`
    const diluent = text(ccr.diluent?.name)
    if (diluent) fields.diluent = diluent
  }

  if (dive.repetitive === true) {
    fields.repetitiveDive = 'yes'
    if (exposure.series) {
      fields.seriesCns = `${exposure.cns} %`
      fields.seriesOtu = String(exposure.otu)
    }
  }
  if (Number.isFinite(dive.surfaceIntervalBefore)) {
    fields.surfaceIntervalBefore = minutesLabel(dive.surfaceIntervalBefore)
  }
  if (dive.heliumInDive === true || dive.heliumSeries === true) fields.helium = 'yes'

  Object.assign(fields, surfaceIntervalFields(dive))

  return {
    title: [APP_NAME, site, `${maxDepth} m / ${bottomTime} min`].filter(Boolean).join(' - '),
    author,
    subject,
    keywords: [
      ...BASE_KEYWORDS,
      algorithm,
      gf ? `GF ${gf}` : '',
      modeLabel,
      ...gases,
      site
    ].filter(Boolean),
    fields
  }
}

/**
 * Metadata of a day of diving: the series as a whole, not its last dive.
 *
 * @param {Object} args
 * @param {Object[]} args.computed - Dives carrying a plan, [{ dive, index, plan }]
 * @param {number} args.total - Number of dives the day holds, computed or not
 * @param {Object} args.settings - Shared settings of the series
 * @param {Object} args.environment - Dive environment of the series
 * @returns {Object} { title, author, subject, keywords, fields }
 */
export function dayPdfMetadata({ computed = [], total = 0, settings = {}, environment = {} } = {}) {
  const site = text(settings?.diveSite)
  const author = text(settings?.diverNames)
  const fields = contextFields(settings, environment)
  const algorithm = algorithmLabel(settings)
  const gf = gfLabel(settings)
  const plans = computed.map(entry => entry.plan).filter(Boolean)

  fields.documentType = 'day of diving'
  fields.diveCount = String(total)
  fields.divesPlanned = String(plans.length)

  if (plans.length === 0) {
    console.error('PdfMetadata.dayPdfMetadata: no dive of the day carries a computed plan, the day is only named')
    return {
      title: [APP_NAME, site, 'day of diving'].filter(Boolean).join(' - '),
      author,
      subject: `${algorithm} day of diving`,
      keywords: [...BASE_KEYWORDS, 'day of diving', algorithm, site].filter(Boolean),
      fields
    }
  }

  // The oxygen clock and the surface interval of a series are carried by its
  // last computed dive, exactly as DayExport reads them for the totals page.
  const last = plans[plans.length - 1]
  const exposure = exposureShown(last)
  const maxDepth = Math.max(...plans.map(plan => getMaxDepth(plan)))
  const bottomTime = plans.reduce((sum, plan) => sum + getBottomTime(plan), 0)

  Object.assign(fields, {
    maxDepth: `${maxDepth} m`,
    totalBottomTime: minutesLabel(bottomTime),
    seriesCns: `${exposure.cns} %`,
    seriesOtu: String(exposure.otu)
  })
  if (plans.some(plan => plan.heliumSeries === true || plan.heliumInDive === true)) fields.helium = 'yes'

  Object.assign(fields, surfaceIntervalFields(last))

  const dives = plans.length === 1 ? '1 dive' : `${plans.length} dives`
  const subject = [
    `Day of diving, ${dives}`,
    `${algorithm} decompression`,
    gf ? `GF ${gf}` : '',
    `max depth ${maxDepth} m`,
    `total bottom time ${bottomTime} min`,
    `series CNS ${exposure.cns} %`,
    `series OTU ${exposure.otu}`
  ].filter(Boolean).join(', ')

  return {
    title: [APP_NAME, site, `day of diving - ${dives}`].filter(Boolean).join(' - '),
    author,
    subject,
    keywords: [
      ...BASE_KEYWORDS,
      'day of diving',
      'repetitive dives',
      algorithm,
      gf ? `GF ${gf}` : '',
      site
    ].filter(Boolean),
    fields
  }
}

// Metadata reduced to what can actually be written: strings, keywords without
// duplicates, and fields whose name is a legal XML name. A field dropped here
// is a bug in a caller, so it is named in the log rather than skipped quietly.
function normalizeMetadata(meta) {
  const source = (meta && typeof meta === 'object') ? meta : {}
  if (!meta || typeof meta !== 'object') {
    console.error('PdfMetadata: no metadata given (%o), the document only carries the application name', meta)
  }

  const keywords = []
  for (const keyword of Array.isArray(source.keywords) ? source.keywords : []) {
    const value = text(keyword)
    if (value && !keywords.includes(value)) keywords.push(value)
  }

  const fields = {}
  for (const [key, raw] of Object.entries(source.fields || {})) {
    const value = text(raw)
    if (!value) continue
    if (!XML_NAME.test(key)) {
      console.error('PdfMetadata: field name "%s" is not a legal XML name, field left out of the XMP packet', key)
      continue
    }
    fields[key] = value
  }

  return {
    title: text(source.title) || PDF_CREATOR,
    subject: text(source.subject),
    author: text(source.author),
    keywords,
    fields
  }
}

/**
 * XMP packet of a document, as raw XML.
 *
 * @param {Object} meta - Normalized metadata
 * @param {Date} date - Creation date of the document
 * @returns {string} XMP packet
 */
export function buildXmpPacket(meta, date = new Date()) {
  const safe = normalizeMetadata(meta)
  const stamp = xmpDate(date)
  const lines = [
    '<?xpacket begin="" id="W5M0MpCehiHzreSzNTczkc9d"?>',
    `<x:xmpmeta xmlns:x="adobe:ns:meta/" x:xmptk="${escapeXml(PDF_CREATOR)}">`,
    ' <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">',
    '  <rdf:Description rdf:about=""',
    '      xmlns:dc="http://purl.org/dc/elements/1.1/"',
    '      xmlns:pdf="http://ns.adobe.com/pdf/1.3/"',
    '      xmlns:xmp="http://ns.adobe.com/xap/1.0/"',
    `      xmlns:mvplan="${escapeXml(MVPLAN_XMP_NS)}">`,
    '   <dc:format>application/pdf</dc:format>',
    `   <dc:title><rdf:Alt><rdf:li xml:lang="x-default">${escapeXml(safe.title)}</rdf:li></rdf:Alt></dc:title>`,
    `   <pdf:Producer>${escapeXml(PDF_PRODUCER)}</pdf:Producer>`,
    `   <xmp:CreatorTool>${escapeXml(PDF_CREATOR)}</xmp:CreatorTool>`,
    `   <xmp:CreateDate>${stamp}</xmp:CreateDate>`,
    `   <xmp:ModifyDate>${stamp}</xmp:ModifyDate>`
  ]

  if (safe.subject) {
    lines.push(`   <dc:description><rdf:Alt><rdf:li xml:lang="x-default">${escapeXml(safe.subject)}</rdf:li></rdf:Alt></dc:description>`)
  }
  if (safe.author) {
    lines.push(`   <dc:creator><rdf:Seq><rdf:li>${escapeXml(safe.author)}</rdf:li></rdf:Seq></dc:creator>`)
  }
  if (safe.keywords.length > 0) {
    const items = safe.keywords.map(keyword => `<rdf:li>${escapeXml(keyword)}</rdf:li>`).join('')
    lines.push(`   <dc:subject><rdf:Bag>${items}</rdf:Bag></dc:subject>`)
    lines.push(`   <pdf:Keywords>${escapeXml(safe.keywords.join(', '))}</pdf:Keywords>`)
  }

  for (const [key, value] of Object.entries(safe.fields)) {
    lines.push(`   <mvplan:${key}>${escapeXml(value)}</mvplan:${key}>`)
  }

  lines.push(
    '  </rdf:Description>',
    ' </rdf:RDF>',
    '</x:xmpmeta>',
    '<?xpacket end="w"?>'
  )

  return lines.join('\n')
}

/**
 * Write the metadata of a document: Info dictionary, then XMP packet.
 *
 * Called once per document, on the document the caller owns. jsPDF keeps the
 * first XMP packet it is handed and ignores every later call, so a document
 * built from several sheets must be given its metadata by whoever created it.
 *
 * @param {Object} doc - jsPDF document
 * @param {Object} meta - Metadata, from divePdfMetadata or dayPdfMetadata
 * @param {Date} [date] - Creation date written in the XMP packet
 * @returns {Object} The document, so the call can be chained
 */
export function applyPdfMetadata(doc, meta, date = new Date()) {
  if (!doc || typeof doc.setProperties !== 'function') {
    console.error('PdfMetadata.applyPdfMetadata: this document cannot carry properties (%o), it keeps the jsPDF defaults', doc)
    return doc
  }

  const safe = normalizeMetadata(meta)

  doc.setProperties({
    title: safe.title,
    subject: safe.subject,
    author: safe.author,
    keywords: safe.keywords.join(', '),
    creator: PDF_CREATOR
  })

  if (typeof doc.addMetadata !== 'function') {
    console.error('PdfMetadata.applyPdfMetadata: this jsPDF build carries no addMetadata (XMP plugin missing), only the Info dictionary is written')
    return doc
  }

  doc.addMetadata(buildXmpPacket(safe, date), true)
  return doc
}
